// Actor index: one small integer per actor, shared by every per-hex array.
//
//   0                  none
//   1 .. maxCountryId  countries, at their Country.id (so owner grids need no remapping)
//   maxCountryId + 1 … curated actors (non-state, blocs, historical) in world.json order,
//                      skipping any whose id is already a country's iso3 ('abkhazia')

import type { Actor, ActorId, Bloc, Country } from '../core/schema.ts';

export interface ActorColors {
  primary: string;
  secondary: string;
}

export interface ActorInfo {
  id: ActorId;
  name: string;
  colors: ActorColors;
  kind: 'country' | Actor['kind'];
}

export interface ActorTable {
  /** By actor index; slot 0 and any unused country id are null. */
  list: Array<ActorInfo | null>;
  byId: Map<ActorId, number>;
  /** Highest country index; indices above it are curated actors. */
  maxCountry: number;
}

/** Drawn for an actor that has no colours of its own (a UI default, not data). */
const FALLBACK_COLORS: ActorColors = { primary: '#8a8f98', secondary: '#1e1e24' };

function actorColors(a: Actor, blocs: Map<ActorId, Bloc>): ActorColors {
  if (a.colors) {
    return a.colors;
  }
  const bloc = blocs.get(a.id);
  if (bloc) {
    return { primary: bloc.color, secondary: FALLBACK_COLORS.secondary };
  }
  return FALLBACK_COLORS;
}

/** Blocs listed only in blocs.json still get an actor entry. */
function blocActors(actors: Actor[], blocs: Bloc[]): Actor[] {
  const known = new Set(actors.map((a) => a.id));
  const extra: Actor[] = blocs
    .filter((b) => !known.has(b.id))
    .map((b) => ({ id: b.id, kind: 'bloc', name: b.name, sources: b.sources }));
  return [...actors, ...extra];
}

export function buildActors(countries: Country[], actors: Actor[], blocs: Bloc[]): ActorTable {
  const maxCountry = countries.reduce((m, c) => Math.max(m, c.id), 0);
  const list: Array<ActorInfo | null> = new Array(maxCountry + 1).fill(null);
  const byId = new Map<ActorId, number>();

  for (const c of countries) {
    list[c.id] = { id: c.iso3, name: c.name, colors: c.colors, kind: 'country' };
    byId.set(c.iso3, c.id);
  }

  const blocById = new Map(blocs.map((b) => [b.id, b]));
  for (const a of blocActors(actors, blocs)) {
    if (byId.has(a.id)) {
      continue;
    }
    byId.set(a.id, list.length);
    list.push({ id: a.id, name: a.name, colors: actorColors(a, blocById), kind: a.kind });
  }
  return { list, byId, maxCountry };
}

/** Index of an actor id, 0 when unknown. */
export function indexOf(t: ActorTable, id: ActorId): number {
  return t.byId.get(id) ?? 0;
}
